import type {
  Me,
  Profile,
  ProfileUpdate,
  Health,
  Problem,
  Page,
  Video,
  Line,
  Scene,
  Gloss,
  GlossRequest,
  GlossAlternateRequest,
  EventBatchRequest,
  EventBatchResponse,
  StudySessionStart,
  ActivityStart,
  ActivityEnd,
  VideoResolveRequest,
  VideoResolveResponse,
  VideoCreateRequest,
  VideoCreateResponse,
  LineUpdate,
  LineSplitRequest,
  LineMergeRequest,
  LineInsertRequest,
  TranscriptMutationResponse,
  Snapshot,
  Job,
  SceneCreate,
  SceneUpdate,
  SavedItem,
  SavedItemCreate,
  TtsAudio,
  TtsRequest,
  Channel,
  Visibility,
  ItemView,
  ItemCreate,
  ItemUpdate,
  ItemKind,
  MemoryStateSummary,
  Lesson,
  LessonIndexRow,
  Coverage,
  TodaySummary,
  CatalogEntry,
  CatalogForms,
  InstantLookup,
  MtTranslation,
  MtTranslateRequest,
  LineRef,
  PatternDraft,
  PatternDraftRequest,
  PatternPreviewRequest,
  PatternPreviewResponse,
  MediaUsage,
  MediaPart,
  MediaUploadCreateRequest,
  MediaUploadCreateResponse,
  MediaUploadCompleteRequest,
  MediaUploadCompleteResponse,
  Exercise,
  ExerciseKind,
  ExerciseAnswerRequest,
  ExerciseAnswerResponse,
  Story,
  StoryGenerateRequest,
  StoryImportRequest,
  StoryRate,
  DailyBite,
  VideoItemsResponse,
  AbandonReason,
  LangPair,
  Composition,
  CompositionUpdate,
  CompositionCheckRequest,
  CompositionCheckResponse,
  Correction,
  Suggestion,
  CorrectionActionRequest,
  Conversation,
  ConversationStartRequest,
  ConversationTurnRequest,
  ConversationTurnResponse,
  ConversationRecap,
  ConversationSummary,
  PlacementAnswerRequest,
  PlacementAnswerResponse,
  PlacementNextRequest,
  PlacementNextResponse,
  ItemCounts,
} from '@nelnela/contracts';

export class ApiError extends Error {
  constructor(public problem: Problem) {
    super(problem.title);
  }
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' });
  if (!res.ok) {
    let p: Problem = { type: 'about:blank', title: res.statusText, status: res.status };
    try {
      p = await res.json();
    } catch {
      /* keep default */
    }
    throw new ApiError(p);
  }
  return res.json() as Promise<T>;
}

/**
 * The subset of the contract's ClientKind this bundle can be. 'pwa' when installed to the home
 * screen (standalone display mode); 'twa'/'import'/'local_video' are other producers. Lives here
 * (not in session.ts) so api.ts stays a leaf module — session.ts and events.ts import api.ts.
 */
export type ClientKind = 'web' | 'pwa';

export function clientKind(): ClientKind {
  try {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia('(display-mode: standalone)').matches ? 'pwa' : 'web';
    }
  } catch {
    /* jsdom / partial environments: fall through */
  }
  return 'web';
}

async function toApiError(res: Response): Promise<ApiError> {
  let p: Problem = { type: 'about:blank', title: res.statusText, status: res.status };
  try {
    p = await res.json();
  } catch {
    /* keep default */
  }
  return new ApiError(p);
}

/** Void routes may answer 200 with an empty body or 204; both resolve to undefined. */
async function parseBody<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

async function send<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body: unknown,
  init?: { keepalive?: boolean },
): Promise<{ status: number; body: T }> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    keepalive: init?.keepalive,
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      'x-nelnela-client': clientKind(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw await toApiError(res);
  return { status: res.status, body: await parseBody<T>(res) };
}

export async function post<T>(
  path: string,
  body: unknown,
  init?: { keepalive?: boolean },
): Promise<T> {
  return (await send<T>('POST', path, body, init)).body;
}

export async function patch<T>(path: string, body: unknown): Promise<T> {
  return (await send<T>('PATCH', path, body)).body;
}

/** For routes where the status carries meaning (eventsBatch: 202 = some events deferred). */
export function postWithStatus<T>(
  path: string,
  body: unknown,
  init?: { keepalive?: boolean },
): Promise<{ status: number; body: T }> {
  return send<T>('POST', path, body, init);
}

export async function del<T = void>(path: string, body?: unknown): Promise<T> {
  return (await send<T>('DELETE', path, body)).body;
}

/**
 * The one request that cannot go through `send()`: the body is raw bytes, not JSON. The Blob is
 * handed to fetch as-is so the browser computes `content-length` itself — the media part route
 * requires it and refuses a part whose length is not the one it expects. `application/octet-stream`
 * because the stored object's real type was declared at create time, not per part.
 */
async function putBytes<T>(path: string, blob: Blob, init?: { signal?: AbortSignal }): Promise<T> {
  const res = await fetch(path, {
    method: 'PUT',
    credentials: 'include',
    signal: init?.signal,
    headers: { 'content-type': 'application/octet-stream', 'x-nelnela-client': clientKind() },
    body: blob,
  });
  if (!res.ok) throw await toApiError(res);
  return parseBody<T>(res);
}

function qs(q: Record<string, string | number | boolean | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined) p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

const enc = encodeURIComponent;

export type VideoWithLines = Video & { lines: Line[]; scenes: Scene[] };

export const api = {
  health: () => get<Health>('/health'),
  me: () => get<Me>('/api/me'),
  profileUpdate: (profileId: string, body: ProfileUpdate) =>
    patch<Profile>(`/api/profiles/${enc(profileId)}`, body),

  // --- library (routes.videoList / videoGet / videoResolve / videoCreate / videoDelete) ---
  videoList: (
    q: {
      scope?: 'shared' | 'mine';
      q?: string;
      limit?: number;
      cursor?: string;
      /** M6a: 'recent' is the default and the only paged order (the others rank on a per-viewer
       *  decoration the cursor cannot encode — see routes/videos.ts). */
      sort?: 'recent' | 'due_density' | 'fit';
    } = {},
  ) =>
    get<Page<Video>>(
      `/api/videos${qs({
        scope: q.scope ?? 'shared',
        q: q.q,
        limit: q.limit,
        cursor: q.cursor,
        sort: q.sort,
      })}`,
    ),
  videoGet: (videoId: string) => get<VideoWithLines>(`/api/videos/${enc(videoId)}`),
  videoResolve: (body: VideoResolveRequest) =>
    post<VideoResolveResponse>('/api/videos/resolve', body),
  videoCreate: (body: VideoCreateRequest) => post<VideoCreateResponse>('/api/videos', body),
  videoDelete: (videoId: string) => del(`/api/videos/${enc(videoId)}`),

  // --- transcript editing (routes.line* / transcript*) ---
  lineUpdate: (transcriptId: string, lineId: string, body: LineUpdate) =>
    patch<TranscriptMutationResponse>(
      `/api/transcripts/${enc(transcriptId)}/lines/${enc(lineId)}`,
      body,
    ),
  lineSplit: (transcriptId: string, lineId: string, body: LineSplitRequest) =>
    post<TranscriptMutationResponse>(
      `/api/transcripts/${enc(transcriptId)}/lines/${enc(lineId)}/split`,
      body,
    ),
  lineMerge: (transcriptId: string, lineId: string, body: LineMergeRequest) =>
    post<TranscriptMutationResponse>(
      `/api/transcripts/${enc(transcriptId)}/lines/${enc(lineId)}/merge`,
      body,
    ),
  lineInsert: (transcriptId: string, body: LineInsertRequest) =>
    post<TranscriptMutationResponse>(`/api/transcripts/${enc(transcriptId)}/lines`, body),
  lineDelete: (transcriptId: string, lineId: string, revision: number) =>
    del<TranscriptMutationResponse>(`/api/transcripts/${enc(transcriptId)}/lines/${enc(lineId)}`, {
      revision,
    }),
  transcriptCleanup: (transcriptId: string) =>
    post<Job>(`/api/transcripts/${enc(transcriptId)}/jobs/cleanup`, undefined),
  transcriptTranslate: (transcriptId: string) =>
    post<Job>(`/api/transcripts/${enc(transcriptId)}/jobs/translate`, undefined),
  transcriptSnapshots: (transcriptId: string) =>
    get<Snapshot[]>(`/api/transcripts/${enc(transcriptId)}/snapshots`),
  transcriptRestore: (transcriptId: string, snapshotId: string) =>
    post<TranscriptMutationResponse>(`/api/transcripts/${enc(transcriptId)}/restore`, {
      snapshotId,
    }),
  /** A URL, not a call: opened in a new tab so the browser downloads the .srt with cookies. */
  transcriptExportSrtUrl: (transcriptId: string, q: { speakers?: boolean; english?: boolean }) =>
    `/api/transcripts/${enc(transcriptId)}/export.srt${qs({ speakers: q.speakers, english: q.english })}`,
  jobGet: (jobId: string) => get<Job>(`/api/jobs/${enc(jobId)}`),

  // --- scenes ---
  sceneList: (videoId: string) => get<Scene[]>(`/api/scenes${qs({ videoId })}`),
  sceneCreate: (body: SceneCreate) => post<Scene>('/api/scenes', body),
  sceneUpdate: (sceneId: string, body: SceneUpdate) =>
    patch<Scene>(`/api/scenes/${enc(sceneId)}`, body),
  sceneDelete: (sceneId: string) => del(`/api/scenes/${enc(sceneId)}`),

  // --- gloss (routes.gloss; the server writes the `lookup` event — the client never does) ---
  gloss: (req: GlossRequest) => post<Gloss>('/api/gloss', req),
  glossAlternate: (req: GlossAlternateRequest) => post<Gloss>('/api/gloss/alternate', req),

  // --- saved / tts ---
  // `storyId` since M6e §3: the reading surfaces ask for the saves of the thing on screen, so the
  // gloss sheet can say "Saved ✓" instead of writing a second identical row.
  savedList: (
    q: {
      videoId?: string;
      storyId?: string;
      itemId?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) => get<Page<SavedItem>>(`/api/saved${qs(q)}`),
  savedCreate: (body: SavedItemCreate) => post<SavedItem>('/api/saved', body),
  savedDelete: (savedItemId: string) => del(`/api/saved/${enc(savedItemId)}`),
  tts: (body: TtsRequest) => post<TtsAudio>('/api/tts', body),

  // --- curator ---
  curatorChannels: (q: { allowlisted?: boolean; limit?: number; cursor?: string } = {}) =>
    get<Page<Channel & { videoCount: number; addRequests: number }>>(
      `/api/curator/channels${qs(q)}`,
    ),
  channelAllowlist: (channelId: string, body: { allowlisted: boolean; notes?: string }) =>
    patch<Channel>(`/api/curator/channels/${enc(channelId)}`, body),
  curatorVideos: (
    q: {
      visibility?: Visibility;
      available?: boolean;
      channelId?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) => get<Page<Video>>(`/api/curator/videos${qs(q)}`),
  videoShare: (videoId: string, visibility: Visibility) =>
    patch<Video>(`/api/curator/videos/${enc(videoId)}/visibility`, { visibility }),
  videoAvailabilityCheck: (videoId: string) =>
    post<Job>(`/api/curator/videos/${enc(videoId)}/check`, undefined),
  /** Re-run the item resolver over a transcript. `videoCreate` and the cleanup finalize enqueue
   *  this on their own — the button is for transcripts added before M4a, and for a re-run after
   *  a new pattern is seeded. */
  transcriptResolve: (transcriptId: string) =>
    post<Job>(`/api/curator/transcripts/${enc(transcriptId)}/resolve`, undefined),
  // --- curator: the scripted library (M6b) ---
  curatorPieces: (q: {
    targetLang: string;
    baseLang: string;
    kind?: string;
    published?: boolean;
    limit?: number;
    cursor?: string;
  }) =>
    get<Page<CuratorPiece>>(
      `/api/curator/pieces${qs({
        ...q,
        published: q.published === undefined ? undefined : String(q.published),
      })}`,
    ),
  curatorPieceTag: (body: { limit?: number; storyIds?: string[]; retag?: boolean } = {}) =>
    post<{
      tagged: number;
      levelled: number;
      checksWritten: number;
      remaining: number;
      skipped: string[];
    }>('/api/curator/pieces/tag', body),
  /** Re-measures `story.level` from the tags a piece already has. No LLM call, no budget gate —
   *  unlike `curatorPieceTag`, which rewrites every tag and pays a lemmatizer call to do it.
   *  No `storyIds` sweeps every shared non-culture piece. */
  curatorPieceRelevel: (body: { storyIds?: string[] } = {}) =>
    post<{ levelled: number; unchanged: number; skipped: string[] }>(
      '/api/curator/pieces/relevel',
      body,
    ),
  curatorPieceWarm: (storyId: string) =>
    post<{
      chars: number;
      cached: number;
      synthesized: number;
      failed: number;
      capRemaining: number;
    }>(`/api/curator/pieces/${enc(storyId)}/warm`, undefined),
  curatorPiecePublish: (storyId: string) =>
    post<Story>(`/api/curator/pieces/${enc(storyId)}/publish`, undefined),
  curatorPieceUnpublish: (storyId: string) =>
    post<Story>(`/api/curator/pieces/${enc(storyId)}/unpublish`, undefined),

  // --- M4a: the tracking engine (items · memory · lessons · coverage) ---
  /** Bulk decoration for the reading surface — one call per transcript, not per word. */
  memoryForItems: (itemIds: string[]) =>
    post<Record<string, MemoryStateSummary>>('/api/memory/lookup', { itemIds }),
  itemList: (
    q: {
      scope?: 'met' | 'mine' | 'verified';
      kind?: ItemKind;
      state?: 'unmet' | 'shaky' | 'solid';
      sort?: 'due' | 'weakest' | 'recent' | 'level';
      q?: string;
      levelMin?: number;
      levelMax?: number;
      limit?: number;
      cursor?: string;
    } = {},
  ) => get<Page<ItemView>>(`/api/items${qs(q)}`),
  /** M10-iii: the scope chips' counts and the band headers'. See `ItemCounts`. */
  itemCounts: () => get<ItemCounts>('/api/items/counts'),
  itemProposals: () => get<Array<ItemView & { evidence: LineRef[] }>>('/api/items/proposals'),
  itemGet: (itemId: string) => get<ItemView>(`/api/items/${enc(itemId)}`),
  itemCreate: (body: ItemCreate) => post<ItemView>('/api/items', body),
  itemUpdate: (itemId: string, body: ItemUpdate) =>
    patch<ItemView>(`/api/items/${enc(itemId)}`, body),
  itemAccept: (itemId: string) => post<ItemView>(`/api/items/${enc(itemId)}/accept`, undefined),
  itemDismiss: (itemId: string) => post<void>(`/api/items/${enc(itemId)}/dismiss`, undefined),
  itemSuppress: (itemId: string) => post<ItemView>(`/api/items/${enc(itemId)}/suppress`, undefined),
  itemUnsuppress: (itemId: string) =>
    post<ItemView>(`/api/items/${enc(itemId)}/unsuppress`, undefined),
  itemNoteSet: (itemId: string, text: string) =>
    send<{ text: string }>('PUT', `/api/items/${enc(itemId)}/note`, { text }).then((r) => r.body),
  itemNoteDelete: (itemId: string) => del(`/api/items/${enc(itemId)}/note`),
  lessonForItem: (itemId: string) => get<Lesson | null>(`/api/items/${enc(itemId)}/lesson`),
  lessonIndex: (q: { levelMin?: number; levelMax?: number } = {}) =>
    get<LessonIndexRow[]>(`/api/lessons${qs(q)}`),
  lessonGet: (lessonId: string) => get<Lesson>(`/api/lessons/${enc(lessonId)}`),
  coverage: (profileId: string) => get<Coverage>(`/api/coverage${qs({ profileId })}`),
  /** M5: the frame around the daily bite — date, level, yesterday's line, ring, streak, chips. */
  todaySummary: (profileId: string) => get<TodaySummary>(`/api/today/summary${qs({ profileId })}`),
  catalogLookup: (lemma: string, senseIndex?: number) =>
    get<CatalogEntry | null>(`/api/catalog${qs({ lemma, senseIndex })}`),
  /**
   * M10-i i-1: the tap's instant layer. Zero vendor calls, so it is `session` on the server and
   * costs nothing to ask — `lib/instant.ts` memoises it per session anyway, because the answer
   * cannot change under the learner within one.
   */
  lookupInstant: (q: { selection: string; sentence?: string }) =>
    get<InstantLookup>(`/api/lookup${qs({ targetLang: 'ka', baseLang: 'en', ...q })}`),
  /** M10-i i-2: a verb's paradigm. `null` until the UniMorph gate passes and the seeder runs —
   *  `InstantLookup.formsAvailable` is how a caller knows before asking. */
  catalogForms: (catalogEntryId: string) =>
    get<CatalogForms | null>(`/api/catalog/forms${qs({ catalogEntryId })}`),
  /** M10-i i-3: the one MT wire (ADR-0014) — journal, gloss-sheet fallback, chat bubble. */
  mtTranslate: (body: MtTranslateRequest) => post<MtTranslation>('/api/mt/translate', body),
  lineGet: (lineId: string) => get<LineRef>(`/api/lines/${enc(lineId)}`),
  patternDraft: (body: PatternDraftRequest) => post<PatternDraft>('/api/items/pattern-draft', body),
  patternPreview: (body: PatternPreviewRequest) =>
    post<PatternPreviewResponse>('/api/items/pattern-preview', body),

  // --- M4c: media (ADR-0006 §5 — the single-learner upload lane) ---
  // Every route here is 404, never 403, when the lane is not enabled for the caller, so an
  // ApiError from any of them is indistinguishable from "no such route". Nothing in the client
  // may call them unless `me.media !== null`. `lib/upload.ts` drives create → parts → complete.
  mediaUsage: () => get<MediaUsage>('/api/media/usage'),
  mediaUploadCreate: (body: MediaUploadCreateRequest) =>
    post<MediaUploadCreateResponse>('/api/media/uploads', body),
  /** Raw bytes, 1-based `n`; `signal` is how Cancel stops the parts already in flight. */
  mediaUploadPart: (uploadId: string, n: number, blob: Blob, init?: { signal?: AbortSignal }) =>
    putBytes<MediaPart>(`/api/media/uploads/${enc(uploadId)}/parts/${n}`, blob, init),
  mediaUploadComplete: (uploadId: string, parts: MediaPart[]) =>
    post<MediaUploadCompleteResponse>(`/api/media/uploads/${enc(uploadId)}/complete`, {
      parts,
    } satisfies MediaUploadCompleteRequest),
  mediaUploadAbort: (uploadId: string) => del(`/api/media/uploads/${enc(uploadId)}`),
  /** A URL, not a call: the <video> element fetches it itself (with the session cookie), and
   *  ranges it — like `transcriptExportSrtUrl`, the browser is the client here, not us. */
  mediaStreamUrl: (mediaId: string) => `/api/media/${enc(mediaId)}/stream`,

  // --- M6a: the activities (exercises · stories · the bite · progress · the vocabulary preview) ---
  /** The number on Practice's Review tile, and (M6e §7) the median a story generation takes.
   *  `session`, so it renders for an unentitled learner. */
  reviewSummary: (profileId: string) =>
    get<{ dueCount: number; storyGenMs: number | null }>(`/api/review/summary${qs({ profileId })}`),
  /** Builds AND persists the run's prompts server-side; the client never composes an exercise. */
  exerciseNext: (body: {
    activityId: string;
    kinds?: ExerciseKind[];
    count?: number;
    exerciseIds?: string[];
    storyId?: string;
    itemIds?: string[];
    answerMode?: 'typed' | 'options';
    /** M10-iv: append phrase cards from `saved_item` AFTER the due plan. */
    includeSaved?: boolean;
  }) => post<Exercise[]>('/api/exercises/next', body),
  /** The server grades and writes the `retrieval_attempt` event; the verdict comes back here. */
  exerciseAnswer: (body: ExerciseAnswerRequest) =>
    post<ExerciseAnswerResponse>('/api/exercises/answer', body),
  /**
   * M10-iii, the placement pair. Both calls carry the WHOLE transcript, because the server keeps
   * none of it — see `routes/placement.ts`. The client is the only thing that remembers a run,
   * which is why abandoning one costs nothing.
   */
  placementNext: (body: PlacementNextRequest) =>
    post<PlacementNextResponse>('/api/placement/next', body),
  placementAnswer: (body: PlacementAnswerRequest) =>
    post<PlacementAnswerResponse>('/api/placement/answer', body),

  storyList: (q: { limit?: number; cursor?: string } = {}) =>
    get<Page<Story>>(`/api/stories${qs(q)}`),
  /** Synchronous and slow by design (one Sonnet call): the caller shows 5c's generating state. */
  storyGenerate: (body: StoryGenerateRequest) => post<Story>('/api/stories/generate', body),
  storyImport: (body: StoryImportRequest) => post<Story>('/api/stories/import', body),
  storyGet: (storyId: string) => get<Story>(`/api/stories/${enc(storyId)}`),
  /** M6b: the shared shelf. Published curated pieces only; `read` is per viewer. */
  libraryPieces: (q: {
    targetLang: string;
    baseLang: string;
    kind?: string;
    levelMin?: number;
    levelMax?: number;
    unread?: boolean;
    limit?: number;
    cursor?: string;
  }) =>
    get<Page<Story>>(
      // `qs` supplies its own leading '?' — see the other callers.
      `/api/library/pieces${qs({ ...q, unread: q.unread === undefined ? undefined : String(q.unread) })}`,
    ),
  storyRate: (storyId: string, body: StoryRate) =>
    post<Story>(`/api/stories/${enc(storyId)}/rate`, body),
  storyDelete: (storyId: string) => del(`/api/stories/${enc(storyId)}`),

  dailyBite: (profileId: string) => get<DailyBite>(`/api/daily-bite${qs({ profileId })}`),
  dailyBiteDismiss: (dailyBiteId: string, planIdx: number) =>
    post<DailyBite>(`/api/daily-bite/${enc(dailyBiteId)}/dismiss`, { planIdx }),
  dailyBiteRebuild: (profileId: string, mode: 'replace' | 'extend' = 'replace') =>
    post<DailyBite>('/api/daily-bite/rebuild', { profileId, mode }),

  /** Fired every ~10 s of playback, on pause, and on pagehide — keep it small. */
  videoProgressPut: (
    videoId: string,
    body: { positionMs: number; durationMs?: number },
    init?: { keepalive?: boolean },
  ) => send<void>('PUT', `/api/videos/${enc(videoId)}/progress`, body, init).then((r) => r.body),
  /** The vocabulary preview. Omit the bounds for the whole episode. */
  videoItems: (videoId: string, q: { startMs?: number; endMs?: number } = {}) =>
    get<VideoItemsResponse>(`/api/videos/${enc(videoId)}/items${qs(q)}`),

  /** The "Left early —" strip. First answer wins; a re-tap is a no-op on the server. */
  activityReason: (activityId: string, reason: AbandonReason) =>
    post<void>(`/api/activities/${enc(activityId)}/reason`, { reason }),

  // --- M6c: the journal (F12 — compositions, and the corrections they produce) ---
  // `correctionAct` lives in this block rather than a family of its own because it is the other
  // half of the correction card: Accept / Edit / Ignore. It is deliberately NOT named
  // `compositionCorrectionAct` — the route is `/corrections/:id/act` for both surfaces, and
  // M6c-ii's chat bubble calls this exact method against a correction on a conversation turn.
  // A composition-shaped name would have forced a second, identical method when chat lands.
  /** `session`, not `entitled` — a learner whose trial has ended can still read what they wrote.
   *  What disables for them is the New entry button, not the list (M6c step 7). */
  compositionList: (q: { limit?: number; cursor?: string } = {}) =>
    get<Page<Composition>>(`/api/compositions${qs(q)}`),
  compositionCreate: (body: LangPair & { title?: string }) =>
    post<Composition>('/api/compositions', body),
  /** Carries the corrections and suggestions of the composition's LATEST version. Their offsets
   *  are into the text of *that* version, not into the live draft, so the editor re-anchors
   *  before it draws a single underline. Having the server anchor them instead would mean
   *  sending it the draft on every read — which is the autosave PATCH, deliberately kept apart
   *  from this so a read costs nothing. */
  compositionGet: (compositionId: string) =>
    get<Composition & { corrections: Correction[]; suggestions: Suggestion[] }>(
      `/api/compositions/${enc(compositionId)}`,
    ),
  /** Autosave: no LLM call, no version, no events (the contract's own rule). `keepalive` is here
   *  for the same reason `videoProgressPut` has it — the final save fires from `pagehide`, and a
   *  plain fetch is cancelled along with the document, losing the last few seconds of a
   *  paragraph. Nothing checked is ever lost that way (the check route carries the whole text in
   *  its own body); what is at risk is only the typing since the last save. */
  compositionUpdate: (
    compositionId: string,
    body: CompositionUpdate,
    init?: { keepalive?: boolean },
  ) =>
    send<Composition>('PATCH', `/api/compositions/${enc(compositionId)}`, body, init).then(
      (r) => r.body,
    ),
  /** One Sonnet call, seconds long — the caller shows `checking…` rather than a spinner, and
   *  debounces on the three conditions in `lib/journal.ts`. A check whose text is unchanged is a
   *  server-side no-op that returns the stored corrections with no new version and no
   *  `vendor_call`, so a debounce that fires once too often costs a round trip and nothing more.
   *  Over budget or past the daily cap it answers 429; the draft is untouched either way. */
  compositionCheck: (compositionId: string, body: CompositionCheckRequest) =>
    post<CompositionCheckResponse>(`/api/compositions/${enc(compositionId)}/check`, body),
  /** 204 with no body. The server writes the `correction_shown` event, idempotent on the
   *  caller's `eventId`, so a re-tap changes nothing — `activityReason`'s precedent. */
  correctionAct: (correctionId: string, body: CorrectionActionRequest) =>
    post<void>(`/api/corrections/${enc(correctionId)}/act`, body),

  // --- M6c-ii: text chat (F13 — conversations, and the corrections on your own bubble) ---
  // There is no `conversationCorrectionAct`: a correction on a chat turn goes through the
  // `correctionAct` above, unchanged. That method was named for both surfaces when the journal
  // shipped, and this block is what that naming was for.
  /** `session`, not `entitled` — the same rule as `compositionList`: a learner whose trial has
   *  ended can still read yesterday's conversations and the corrections in them. What the
   *  entitlement gates is `conversationStart`, i.e. the New chat button, not the history. */
  conversationList: (q: { limit?: number; cursor?: string } = {}) =>
    get<Page<ConversationSummary>>(`/api/conversations${qs(q)}`),
  /**
   * 201, and the conversation comes back with its opening turn already in `turns` — the app
   * speaks first, so this makes one Haiku call and is seconds long. The caller shows its own
   * busy state and seeds `['conversation', id]` from the answer, which is why the run screen
   * renders the first bubble and its three chips without a second round trip.
   *
   * `mode: 'spoken'` answers **403**, always. Do not offer it and catch the refusal: the same
   * verdict is on `me.capabilities[lang].spokenDialogue`, and the sheet hides the row when it is
   * false — a locked door the learner never sees is better than one they walk into.
   */
  conversationStart: (body: ConversationStartRequest) =>
    post<Conversation>('/api/conversations', body),
  conversationGet: (conversationId: string) =>
    get<Conversation>(`/api/conversations/${enc(conversationId)}`),
  /**
   * One turn in, both turns out (`{ learnerTurn, appTurn }`) — there is no streaming, so the
   * caller renders its own message optimistically and shimmers the app's side.
   *
   * **Idempotent on `body.eventId`.** The server derives the learner turn's primary key from that
   * ULID, so a replay returns the STORED pair rather than buying a second reply. Which makes the
   * client's rule the important half: generate one ULID per distinct message and reuse it when
   * re-sending after a failure. A fresh ULID on retry is a second conversation turn, a second
   * Haiku call and a second charge, and nothing on the server can tell it apart from a learner
   * who typed the same sentence twice.
   *
   * 409 once the conversation has ended, 429 past the turn cap ("This conversation has run
   * long" — nothing failed), 429 again when the day's chat budget is spent.
   */
  conversationTurn: (conversationId: string, body: ConversationTurnRequest) =>
    post<ConversationTurnResponse>(`/api/conversations/${enc(conversationId)}/turns`, body),
  /** Stamps `ended_at`, kicks the fold, and returns the conversation **with its recap** — which is
   *  derived from the stored turns and corrections, never generated, so this costs no model call
   *  however long the conversation was. A second `end` re-derives the same recap and leaves the
   *  timestamps alone, so a retry whose answer was never seen is safe. */
  conversationEnd: (conversationId: string) =>
    post<Conversation & { recap: ConversationRecap }>(
      `/api/conversations/${enc(conversationId)}/end`,
      undefined,
    ),

  // --- events / sessions / activities ---
  /** 200 = every event accounted for; 202 = some ids not mentioned yet (FK not landed): retry them. */
  eventsBatch: (req: EventBatchRequest, init?: { keepalive?: boolean }) =>
    postWithStatus<EventBatchResponse>('/api/events', req, init),
  sessionStart: (body: StudySessionStart) => post<{ id: string }>('/api/sessions', body),
  sessionEnd: (sessionId: string, body: { endedAt: string }, init?: { keepalive?: boolean }) =>
    post<void>(`/api/sessions/${enc(sessionId)}/end`, body, init),
  activityStart: (body: ActivityStart) => post<{ id: string }>('/api/activities', body),
  activityEnd: (activityId: string, body: ActivityEnd, init?: { keepalive?: boolean }) =>
    post<void>(`/api/activities/${enc(activityId)}/end`, body, init),
};

/**
 * A row of the curator's piece table (M6b). Not `Story`: the curator needs the counts that decide
 * what to do next — how many sentences, how many tags, how much audio is cached — and does not
 * need the sentences themselves, which for 45 pieces would be a megabyte of Georgian nobody reads.
 */
export interface CuratorPiece {
  id: string;
  slug: string | null;
  kind: string;
  title: string | null;
  topic: string | null;
  level: number | null;
  voices: Record<string, string> | null;
  publishedAt: string | null;
  sentences: number;
  tagged: number;
  warmed: number;
  speakers: string[];
  createdAt: string;
}
